import { DndContext, DragEndEvent, Modifier } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';
import { logger } from '@lessonup/logging-classic';
import { SortingQuestionAnswerAreaWrapper, useUpdatePin } from '@lessonup/pin-renderer';
import {
  getCorrectSortingQuestionPinOrder,
  getSortingQuestionAnswerOrderGenerator,
  SortingQuestionTeacherPin,
} from '@lessonup/pins-shared';
import React, { useCallback } from 'react';
import { useEditorTranslation } from '../../../../../hooks/useEditorTranslation';
import { removeAnswerAction, reorderCorrectAnswerAction } from '../../../../../utils/pin/sortingQuestionPinSettings';
import { SortableAnswerCard } from './SortableAnswerCard';

export interface SortingQuestionAnswerAreaContentEditorProps {
  pin: SortingQuestionTeacherPin;
  isPinComponentActive?: boolean;
  scale?: number;
  isInteractive?: boolean;
}

export const SortingQuestionAnswerAreaContentEditor: React.FC<SortingQuestionAnswerAreaContentEditorProps> = ({
  pin,
  isPinComponentActive,
  scale = 1,
  isInteractive,
}) => {
  const { t } = useEditorTranslation();
  const { answers } = pin.settings;
  const correctOrderIds = getCorrectSortingQuestionPinOrder(pin);
  const orderedAnswers = correctOrderIds.map((id) => [id, answers[id]] as const);

  const dispatch = useUpdatePin();

  const generator = getSortingQuestionAnswerOrderGenerator(Object.values(answers).map((answer) => answer.correctOrder));

  const scaleCorrectionModifier = useScaleCorrectionModifier(scale);

  const dropItem = (event: DragEndEvent) => {
    const { active, over, delta } = event;
    if (!over || over.id === active.id) return;

    const overOriginalOrder = answers[over.id]?.correctOrder;

    if (!overOriginalOrder) {
      logger.error(`Item dropped over answer not found in list: ${over.id}`);
      return;
    }

    if (delta.y < 0) {
      // Dragged upwards, we put active before over
      dispatch(reorderCorrectAnswerAction(active.id, generator.keyBefore(overOriginalOrder), pin.settings));
    } else {
      // Dragged downwards, we put active after over
      dispatch(reorderCorrectAnswerAction(active.id, generator.keyAfter(overOriginalOrder), pin.settings));
    }
  };

  return (
    <SortingQuestionAnswerAreaWrapper description={t('answerAreaPinComponent.sortingQuestion.instruction')}>
      <DndContext
        onDragEnd={dropItem}
        modifiers={[restrictToVerticalAxis, restrictToParentElement, scaleCorrectionModifier]}
      >
        <SortableContext items={correctOrderIds}>
          {orderedAnswers.map(([id, answer], index) => (
            <SortableAnswerCard
              key={id}
              answerId={id}
              index={index}
              answer={answer}
              pin={pin}
              isPinComponentActive={isPinComponentActive}
              onRemove={() => dispatch(removeAnswerAction(id, pin.settings))}
              isInteractive={isInteractive}
            />
          ))}
        </SortableContext>
      </DndContext>
    </SortingQuestionAnswerAreaWrapper>
  );
};

/**
 * Custom dnd modifier to correct with the scale of the pin when dragging an item.
 */
function useScaleCorrectionModifier(scale: number): Modifier {
  return useCallback<Modifier>(
    ({ transform }) => {
      if (scale === 1 || scale <= 0) return transform;
      return {
        ...transform,
        x: transform.x / scale,
        y: transform.y / scale,
      };
    },
    [scale]
  );
}
